import type { HistoricalSource } from "../types";

interface EvidenceBlockProps {
  source: HistoricalSource | null;
  witness: "author_present" | "author_secondhand" | "record" | "reconstruction" | null;
  quote: string | null;
  /** Editor-authored full account of the evening; when present it replaces the bare `quote`
   *  blockquote, with `quote` highlighted inside it. See HistoricalConcert.evidence_excerpt. */
  excerpt?: string | null;
  locator: string | null;
}

/** Split `excerpt` around the first occurrence of `quote` so the grounded claim sentence
 *  can be emphasised inside the fuller passage. The stored `quote` and the excerpt are both
 *  verbatim slices of the same source but can differ in incidental whitespace and in
 *  punctuation variants an LLM folds (curly vs straight quotes, en-dash vs hyphen), so the
 *  match is built as a whitespace- and punctuation-flexible regex. Best-effort: returns null
 *  when the quote isn't locatable, and the caller then renders the excerpt plain. */
function splitAroundQuote(
  excerpt: string,
  quote: string | null,
): { before: string; match: string; after: string } | null {
  const trimmed = (quote || "").trim();
  if (!trimmed) return null;
  const pattern = trimmed
    .replace(/[.*+?^${}()|[\]\\]/g, "\\$&") // escape regex metachars
    .replace(/["'‘’“”]/g, "[\"'‘’“”]") // any quote glyph matches any other
    .replace(/[-‐-—−]/g, "[-‐-—−]") // any dash glyph matches any other
    .replace(/\s+/g, "\\s+"); // whitespace runs are flexible
  const m = new RegExp(pattern).exec(excerpt);
  if (!m) return null;
  return {
    before: excerpt.slice(0, m.index),
    match: m[0],
    after: excerpt.slice(m.index + m[0].length),
  };
}

const RELIABILITY_LABEL: Record<string, string> = {
  institutional_record: "Institutional record",
  eyewitness: "Eyewitness account",
  contemporary_press: "Contemporary press",
  scholarly: "Scholarly source",
  crowdsourced: "Crowdsourced",
};

/** "CC0-1.0" → "CC0", "CC-BY-4.0" → "CC-BY": the version suffix is real metadata (kept in
 *  full in `source.license` and the tooltip) but adds nothing to a one-line citation. */
function shortLicense(license: string): string {
  return license.replace(/-\d+(\.\d+)*$/, "");
}

/** The citation/evidence block — the product's answer to "how do we know this?" for a
 *  concert nobody scraped from a live website.
 *
 *  Two variants, selected by whether `quote` is present (see api/export.py's comment on the
 *  same fork): an institutional archive row (the H1/NY Phil norm today, `quote === null`)
 *  renders a plain source line; a text-source mention with a verbatim, grounding-verified
 *  quote (`historical_mentions.quote` — Tier 3, not ingested yet, but the shape is real:
 *  see docs/2026-07-16-historical-concerts-plan.md §4) renders the quote itself plus its
 *  locator. Same component either way — a future memoir mention needs no new UI, only data. */
export function EvidenceBlock({ source, witness, quote, excerpt, locator }: EvidenceBlockProps) {
  if (!source) return null;

  const passage = excerpt || quote;
  if (passage) {
    const verb = witness === "author_secondhand" ? "recounts" : "writes";
    const byline = source.author ? `${source.author} ${verb}` : "The source states";
    // When we have the fuller excerpt, highlight the tight grounded claim quote inside it.
    const split = excerpt ? splitAroundQuote(excerpt, quote) : null;
    return (
      <div class="evidence-block evidence-quote">
        <blockquote class={`evidence-quote-text${excerpt ? " evidence-excerpt" : ""}`}>
          &ldquo;
          {split ? (
            <>
              {split.before}
              <mark class="evidence-claim">{split.match}</mark>
              {split.after}
            </>
          ) : (
            passage
          )}
          &rdquo;
        </blockquote>
        <div class="evidence-citation">
          <span class="evidence-byline">{byline}</span>
          {" — "}
          <cite>{source.title}</cite>
          {locator && <span class="evidence-locator">, {locator}</span>}
        </div>
      </div>
    );
  }

  const reliability = source.reliability ? RELIABILITY_LABEL[source.reliability] : null;
  return (
    <div
      class="evidence-block evidence-record"
      title={source.url ? undefined : `${source.title}${source.author ? ` — ${source.author}` : ""}`}
    >
      <svg class="evidence-icon" viewBox="0 0 24 24" width="12" height="12" aria-hidden="true">
        <path
          fill="currentColor"
          d="M6 2h9l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2zm8 1.5V8h4.5L14 3.5zM7 12h10v1.5H7V12zm0 3.5h10V17H7v-1.5zm0-7h6V10H7V8.5z"
        />
      </svg>
      <span class="evidence-text">
        Source:{" "}
        {source.url ? (
          <a href={source.url} target="_blank" rel="noopener noreferrer">
            {source.title}
          </a>
        ) : (
          source.title
        )}
        {source.license && ` (${shortLicense(source.license)})`}
      </span>
      {reliability && <span class="evidence-reliability">{reliability}</span>}
    </div>
  );
}
